import React from 'react';

export interface VatBreakdownRow {
  vat_rate: number | string;
  base: number | string;
  vat_amount: number | string;
}

interface Props {
  breakdown?: VatBreakdownRow[] | null;
  baseZero?: number | string | null;
  baseNeobsahuje?: number | string | null;
  totalAmount?: number | string | null;
  currency?: string | null;
}

const n = (v: number | string | null | undefined): number => {
  if (v === null || v === undefined || v === '') return NaN;
  return typeof v === 'number' ? v : parseFloat(v);
};
const SYMBOL: Record<string, string> = { EUR: '€', USD: '$', GBP: '£', CZK: 'Kč', PLN: 'zł', HUF: 'Ft', CHF: 'CHF' };
let fmtCurrency = 'EUR';
const fmt = (v: number | string | null | undefined): string => {
  const x = n(v);
  const code = (fmtCurrency || 'EUR').toUpperCase();
  return Number.isNaN(x) ? '—' : `${x.toFixed(2)} ${SYMBOL[code] || code}`;
};

// DPH rozpis po sadzbách (multi-rate). Zobrazí kladné sadzby z document_vat_breakdown
// + voliteľne 0 % a „neobsahuje DPH" (neplatca). Nič nezobrazí, ak nie sú dáta.
const VatBreakdownPanel: React.FC<Props> = ({ breakdown, baseZero, baseNeobsahuje, totalAmount, currency }) => {
  fmtCurrency = currency || 'EUR';
  const rows = Array.isArray(breakdown) ? breakdown : [];
  const hasZero = !Number.isNaN(n(baseZero)) && n(baseZero) > 0;
  const hasNeob = !Number.isNaN(n(baseNeobsahuje)) && n(baseNeobsahuje) > 0;

  if (rows.length === 0 && !hasZero && !hasNeob) return null;

  const baseSum = rows.reduce((s, r) => s + (Number.isNaN(n(r.base)) ? 0 : n(r.base)), 0);
  const vatSum = rows.reduce((s, r) => s + (Number.isNaN(n(r.vat_amount)) ? 0 : n(r.vat_amount)), 0);
  const baseAll = baseSum + (hasZero ? n(baseZero) : 0) + (hasNeob ? n(baseNeobsahuje) : 0);
  // Celková suma z OCR sa v DB neukladá — keď ju nemáme, dopočítame základy + DPH.
  const total = !Number.isNaN(n(totalAmount)) ? n(totalAmount) : Math.round((baseAll + vatSum) * 100) / 100;

  return (
    <div style={{ marginBottom: '1.5rem' }}>
      <h3 style={{ margin: '0 0 0.5rem', color: '#1f2937', fontSize: '0.95rem' }}>DPH rozpis</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
            <th style={th}>Sadzba</th>
            <th style={{ ...th, textAlign: 'right' }}>Základ</th>
            <th style={{ ...th, textAlign: 'right' }}>DPH</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={td}>{n(r.vat_rate)} %</td>
              <td style={tdNum}>{fmt(r.base)}</td>
              <td style={tdNum}>{fmt(r.vat_amount)}</td>
            </tr>
          ))}
          {hasZero && (
            <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={td}>0 %</td>
              <td style={tdNum}>{fmt(baseZero)}</td>
              <td style={tdNum}>—</td>
            </tr>
          )}
          {hasNeob && (
            <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
              <td style={td}>neplatca DPH</td>
              <td style={tdNum}>{fmt(baseNeobsahuje)}</td>
              <td style={tdNum}>—</td>
            </tr>
          )}
          {rows.length > 0 && (
            <tr style={{ borderTop: '2px solid #e5e7eb', fontWeight: 600 }}>
              <td style={td}>Spolu</td>
              <td style={tdNum}>{fmt(baseAll)}</td>
              <td style={tdNum}>{fmt(vatSum)}</td>
            </tr>
          )}
        </tbody>
      </table>
      <div style={{ fontSize: 13, color: '#374151', marginTop: 6, textAlign: 'right' }}>
        Celkom s DPH: <strong>{fmt(total)}</strong>
      </div>
    </div>
  );
};

const th: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', fontWeight: 600, fontSize: 12,
  color: '#374151', textTransform: 'uppercase', letterSpacing: 0.3,
};
const td: React.CSSProperties = { padding: '8px 10px', color: '#1f2937' };
const tdNum: React.CSSProperties = {
  padding: '8px 10px', textAlign: 'right', whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums', color: '#1f2937',
};

export default VatBreakdownPanel;
